import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Avatar,
  message,
  Upload,
  Row,
  Col,
  Divider,
  Typography,
} from "antd";
import {
  UserOutlined,
  UploadOutlined,
  EditOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import apiClient from "../utils/apiClient";

const { Title, Text } = Typography;

const SuperAdminProfile = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleProfileUpdate = async (values) => {
    setLoading(true);
    try {
      await apiClient.put("/superadmin/profile", values);
      message.success("Profile updated successfully");
    } catch (error) {
      message.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values) => {
    setPasswordLoading(true);
    try {
      await apiClient.put("/superadmin/change-password", values);
      message.success("Password changed successfully");
      passwordForm.resetFields();
    } catch (error) {
      message.error("Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const uploadProps = {
    name: "avatar",
    action: "/upload/avatar",
    headers: {
      authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    onChange(info) {
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };

  return (
    <div>
      <Title level={2}>SuperAdmin Profile</Title>

      <Row gutter={16}>
        <Col span={12}>
          <Card
            title={
              <>
                <UserOutlined /> Profile Information
              </>
            }
          >
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Avatar size={100} icon={<UserOutlined />} />
              <div style={{ marginTop: 16 }}>
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Change Avatar</Button>
                </Upload>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleProfileUpdate}
              initialValues={{
                name: user?.name || "SuperAdmin",
                email: user?.email || "superadmin@travnok.com",
                phone: "+1 (555) 123-4567",
                title: "System Administrator",
                department: "IT Operations",
              }}
            >
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input size="large" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input size="large" />
              </Form.Item>

              <Form.Item name="phone" label="Phone Number">
                <Input size="large" />
              </Form.Item>

              <Form.Item name="title" label="Job Title">
                <Input size="large" />
              </Form.Item>

              <Form.Item name="department" label="Department">
                <Input size="large" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  icon={<EditOutlined />}
                >
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <>
                <LockOutlined /> Change Password
              </>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
            >
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[
                  {
                    required: true,
                    message: "Please enter your current password",
                  },
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: "Please enter a new password" },
                  { min: 8, message: "Password must be at least 8 characters" },
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={["newPassword"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm your new password",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={passwordLoading}
                  size="large"
                  icon={<LockOutlined />}
                >
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="Account Information">
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Role:</Text>
                <br />
                <Text>SuperAdmin</Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Account Created:</Text>
                <br />
                <Text>{new Date().toLocaleDateString()}</Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Last Login:</Text>
                <br />
                <Text>{new Date().toLocaleString()}</Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Account Status:</Text>
                <br />
                <Text style={{ color: "#52c41a" }}>Active</Text>
              </div>

              <Divider />

              <div style={{ marginBottom: 16 }}>
                <Text strong>Permissions:</Text>
                <br />
                <Text>Full System Access</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminProfile;
